import type { JSX } from "react";

import { BackgroundImage, Badge, Button, Card, Flex, Image, Text } from "@mantine/core";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { ChallengeInfo } from "@/sales-tools/challenges/api/getChallengesForEmployee";

import { getBadgeIcon } from "@/sales-tools/challenges/utils/getBadgeIcon";
import { capitalizeFirstLetter } from "@/sales-tools/home-product/components/PlanHighlights/capitalizeFirstLetter";
import { RightArrowIcon } from "@/sales-tools/icons/RightArrowIcon";

import purpleBackground from "../../icons/PurpleBackground.png?url";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Chicago");

export function AnnouncementStartCover({
    challenge,
    onViewDetailsClick,
}: {
    challenge: ChallengeInfo;
    onViewDetailsClick: () => void;
}): JSX.Element {
    const { challengeType, startDate, badgeType } = challenge;
    const { height, width } = getBadgeSize(badgeType);
    return (
        <Card w="450px" shadow="sm" p={0} radius="md" withBorder>
            <BackgroundImage src={purpleBackground} h={160}>
                <Flex justify="center" align="center" h={160}>
                    <Image data-testid="badge-type" src={getBadgeIcon(badgeType)} h={height} w={width} />
                </Flex>
            </BackgroundImage>
            <Flex direction="column" align="center" justify="center" mt="md" mb="xs">
                <Badge
                    c="var(--mantine-color-dark-9)"
                    color="var(--mantine-color-success-5)"
                    fw="var(--mantine-font-weight-heavy)"
                    fz="xs"
                    p="3xs"
                    radius="xs"
                    tt="none"
                >
                    New
                </Badge>
                <Text
                    data-testid="announcement-challenge-type"
                    size="lg"
                    mt="xxs"
                    mb="xxs"
                    fw="var(--mantine-font-weight-heavy)"
                >
                    {capitalizeFirstLetter(challengeType)} challenge
                </Text>
                <Text data-testid="announcement-header" size="md" fw={500}>
                    {dayjs.utc(startDate).tz("America/Chicago").isBefore(dayjs()) ? "Started" : "Starts"}{" "}
                    {dayjs.utc(startDate).tz("America/Chicago").format("MMMM D")}
                </Text>
                <Button
                    data-testid="announcement-view-details"
                    onClick={onViewDetailsClick}
                    rightSection={<RightArrowIcon fillColor="var(--mantine-color-primary-2)" />}
                    mt="xl"
                    maw="155px"
                    radius="xl"
                >
                    <Text size="sm" fw="var(--mantine-font-weight-heavy)">
                        View details
                    </Text>
                </Button>
            </Flex>
        </Card>
    );
}

function getBadgeSize(badgeType: string): { height: number; width: number } {
    switch (badgeType) {
        case "SOLO_ANIMATED_BADGE":
        case "TEAM_ANIMATED_BADGE":
            return {
                height: 150,
                width: 150,
            };
        default:
            return {
                height: 100,
                width: 100,
            };
    }
}
